import { HttpErrorResponse } from '@angular/common/http';
import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ChipModule } from 'primeng/chip';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { LocalizationService } from '../../core/localization/localization.service';
import { CultureSelectorComponent } from '../../shared/design-system/culture-selector.component';
import { AinFormFieldComponent } from '../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { GrowthBrandVoiceProfileDetail, GrowthJourneyService } from './growth-journey.service';
import {
  BRAND_VOICE_STATUS_I18N_KEYS,
  BRAND_VOICE_TONE_PRESET_OPTIONS,
  brandVoiceStatusSeverity as resolveBrandVoiceStatusSeverity,
  brandVoiceTonePresetI18nKey,
  brandVoiceTonePresetNameFromNumeric,
  brandVoiceTonePresetNumericFromName,
  normalizeBrandVoiceProfileStatus
} from './growth-status.util';

interface ToneAttributes {
  formality: number;
  humor: number;
  empathy: number;
  authority: number;
}

@Component({
  standalone: true,
  selector: 'ain-growth-brand-voice-detail',
  providers: [MessageService],
  imports: [
    FormsModule,
    RouterLink,
    AinPageComponent,
    AinStateComponent,
    AinFormFieldComponent,
    InfoHintComponent,
    CultureSelectorComponent,
    ButtonModule,
    InputTextModule,
    TextareaModule,
    SelectModule,
    InputNumberModule,
    ChipModule,
    TagModule
  ],
  template: `
    <ain-page
      [eyebrow]="t('growth.eyebrow')"
      [title]="pageTitle()"
      [description]="t('growth.journey.brandVoice.detail.description')">
      <div class="toolbar">
        <a pButton routerLink="/growth/journey/brand-voice-profiles" severity="secondary" [label]="t('common.back')"></a>
      </div>

      <ain-info-hint
        [titleKey]="'growth.journey.brandVoice.help.title'"
        [descriptionKey]="'growth.journey.brandVoice.help.description'"
        [bulletKeys]="['growth.journey.brandVoice.help.bullets.tonePreset']" />

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="t('common.error')" [message]="error() ?? ''" />
      } @else {
        <section class="card">
          @if (profile(); as item) {
            <div class="meta-row">
              <p-tag [value]="statusLabel(item.status)" [severity]="statusSeverity(item.status)" />
              @if (item.isDefault) {
                <p-tag [value]="t('growth.journey.brandVoice.defaultBadge')" severity="info" />
              }
            </div>
          }

          <div class="grid">
            <ain-form-field [label]="t('growth.journey.brandVoice.fields.name')">
              <input pInputText class="text-input" [(ngModel)]="model.name" [disabled]="isArchived()" />
            </ain-form-field>
            <ain-form-field [label]="t('growth.journey.brandVoice.fields.tonePreset')">
              <p-select
                [options]="tonePresetOptions()"
                optionLabel="label"
                optionValue="numeric"
                [(ngModel)]="model.tonePresetNumeric"
                [disabled]="isArchived()"
                appendTo="body" />
            </ain-form-field>
            <ain-form-field [label]="t('growth.journey.brandVoice.fields.locale')">
              <ain-culture-selector [(ngModel)]="model.locale" [showClear]="false" [disabled]="isArchived()" />
            </ain-form-field>
          </div>

          <div class="section-title">
            <h3>{{ t('growth.journey.brandVoice.sections.toneAttributes') }}</h3>
          </div>
          <div class="grid tone-grid">
            <ain-form-field [label]="t('growth.journey.brandVoice.attributes.formality')">
              <p-inputNumber [(ngModel)]="toneAttributes.formality" [min]="0" [max]="10" [disabled]="isArchived()" />
            </ain-form-field>
            <ain-form-field [label]="t('growth.journey.brandVoice.attributes.humor')">
              <p-inputNumber [(ngModel)]="toneAttributes.humor" [min]="0" [max]="10" [disabled]="isArchived()" />
            </ain-form-field>
            <ain-form-field [label]="t('growth.journey.brandVoice.attributes.empathy')">
              <p-inputNumber [(ngModel)]="toneAttributes.empathy" [min]="0" [max]="10" [disabled]="isArchived()" />
            </ain-form-field>
            <ain-form-field [label]="t('growth.journey.brandVoice.attributes.authority')">
              <p-inputNumber [(ngModel)]="toneAttributes.authority" [min]="0" [max]="10" [disabled]="isArchived()" />
            </ain-form-field>
          </div>

          <ain-form-field [label]="t('growth.journey.brandVoice.fields.doList')">
            <div class="chip-editor">
              <div class="chip-row">
                @for (item of doList; track $index) {
                  <p-chip [label]="item" [removable]="!isArchived()" (onRemove)="removeDoItem($index)" />
                }
              </div>
              @if (!isArchived()) {
                <div class="chip-input-row">
                  <input
                    pInputText
                    class="text-input"
                    [(ngModel)]="doDraft"
                    [placeholder]="t('growth.journey.brandVoice.placeholders.doItem')"
                    (keydown.enter)="addDoItem($event)" />
                  <button pButton type="button" severity="secondary" size="small" [label]="t('common.add')" (click)="addDoItem()"></button>
                </div>
              }
            </div>
          </ain-form-field>

          <ain-form-field [label]="t('growth.journey.brandVoice.fields.dontList')">
            <div class="chip-editor">
              <div class="chip-row">
                @for (item of dontList; track $index) {
                  <p-chip [label]="item" [removable]="!isArchived()" (onRemove)="removeDontItem($index)" />
                }
              </div>
              @if (!isArchived()) {
                <div class="chip-input-row">
                  <input
                    pInputText
                    class="text-input"
                    [(ngModel)]="dontDraft"
                    [placeholder]="t('growth.journey.brandVoice.placeholders.dontItem')"
                    (keydown.enter)="addDontItem($event)" />
                  <button pButton type="button" severity="secondary" size="small" [label]="t('common.add')" (click)="addDontItem()"></button>
                </div>
              }
            </div>
          </ain-form-field>

          <ain-form-field [label]="t('growth.journey.brandVoice.fields.sampleCopy')">
            <textarea pTextarea rows="4" class="text-input" [(ngModel)]="model.sampleCopy" [disabled]="isArchived()"></textarea>
          </ain-form-field>

          @if (actionError()) {
            <p class="action-error">{{ actionError() }}</p>
          }

          <div class="actions">
            @if (isNew()) {
              <button pButton type="button" [label]="t('common.create')" [loading]="saving()" (click)="create()"></button>
            } @else if (profile()) {
              <button
                pButton
                type="button"
                severity="secondary"
                [label]="t('common.save')"
                [loading]="saving()"
                [disabled]="isArchived()"
                (click)="save()"></button>
              @if (canActivate()) {
                <button pButton type="button" [label]="t('growth.journey.brandVoice.actions.activate')" [loading]="activating()" (click)="activate()"></button>
              }
              @if (canSetDefault()) {
                <button
                  pButton
                  type="button"
                  severity="help"
                  [label]="t('growth.journey.brandVoice.actions.setDefault')"
                  [loading]="settingDefault()"
                  (click)="setDefault()"></button>
              }
              @if (canArchive()) {
                <button
                  pButton
                  type="button"
                  severity="danger"
                  [label]="t('growth.journey.brandVoice.actions.archive')"
                  [loading]="archiving()"
                  (click)="archive()"></button>
              }
            }
          </div>
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .toolbar { margin-bottom: 12px; }
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; display: grid; gap: 12px; }
    .grid { display: grid; gap: 12px; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); }
    .tone-grid { grid-template-columns: repeat(auto-fit, minmax(160px, 1fr)); }
    .section-title h3 { margin: 0; }
    .meta-row { display: flex; gap: 8px; flex-wrap: wrap; }
    .actions { display: flex; gap: 8px; flex-wrap: wrap; }
    .text-input { width: 100%; }
    .chip-editor { display: grid; gap: 8px; }
    .chip-row { display: flex; gap: 6px; flex-wrap: wrap; }
    .chip-input-row { display: flex; gap: 8px; align-items: center; }
    .action-error { margin: 0; color: #b91c1c; }
  `]
})
export class GrowthBrandVoiceDetailComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly journey = inject(GrowthJourneyService);
  private readonly i18n = inject(LocalizationService);
  private readonly messages = inject(MessageService);

  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly isNew = signal(false);
  readonly profile = signal<GrowthBrandVoiceProfileDetail | null>(null);
  readonly saving = signal(false);
  readonly activating = signal(false);
  readonly settingDefault = signal(false);
  readonly archiving = signal(false);
  readonly actionError = signal<string | null>(null);

  model = {
    name: '',
    tonePresetNumeric: 11,
    locale: 'en-US',
    sampleCopy: ''
  };

  toneAttributes: ToneAttributes = {
    formality: 5,
    humor: 3,
    empathy: 5,
    authority: 5
  };

  doList: string[] = [];
  dontList: string[] = [];
  doDraft = '';
  dontDraft = '';

  ngOnInit(): void {
    this.route.paramMap.subscribe(params => {
      const profileId = params.get('profileId');
      this.isNew.set(!profileId || profileId === 'new');
      this.actionError.set(null);

      if (this.isNew()) {
        this.profile.set(null);
        this.loading.set(false);
        return;
      }

      this.loadProfile(profileId!);
    });
  }

  pageTitle(): string {
    return this.isNew()
      ? this.t('growth.journey.brandVoice.actions.new')
      : (this.profile()?.name ?? this.t('growth.journey.brandVoice.title'));
  }

  tonePresetOptions(): Array<{ numeric: number; label: string }> {
    return BRAND_VOICE_TONE_PRESET_OPTIONS.map(option => ({
      numeric: option.numeric,
      label: this.t(option.i18nKey)
    }));
  }

  statusLabel(status: unknown): string {
    const normalized = normalizeBrandVoiceProfileStatus(status);
    return this.t(BRAND_VOICE_STATUS_I18N_KEYS[normalized]);
  }

  statusSeverity(status: unknown): 'secondary' | 'info' | 'success' | 'warn' | 'danger' | 'contrast' {
    return resolveBrandVoiceStatusSeverity(status);
  }

  isArchived(): boolean {
    return normalizeBrandVoiceProfileStatus(this.profile()?.status) === 'Archived';
  }

  canActivate(): boolean {
    return normalizeBrandVoiceProfileStatus(this.profile()?.status) === 'Draft';
  }

  canSetDefault(): boolean {
    const status = normalizeBrandVoiceProfileStatus(this.profile()?.status);
    return status === 'Active' && !this.profile()?.isDefault;
  }

  canArchive(): boolean {
    return normalizeBrandVoiceProfileStatus(this.profile()?.status) !== 'Archived';
  }

  addDoItem(event?: Event): void {
    event?.preventDefault();
    const value = this.doDraft.trim();
    if (!value) {
      return;
    }

    this.doList = [...this.doList, value];
    this.doDraft = '';
  }

  removeDoItem(index: number): void {
    this.doList = this.doList.filter((_, i) => i !== index);
  }

  addDontItem(event?: Event): void {
    event?.preventDefault();
    const value = this.dontDraft.trim();
    if (!value) {
      return;
    }

    this.dontList = [...this.dontList, value];
    this.dontDraft = '';
  }

  removeDontItem(index: number): void {
    this.dontList = this.dontList.filter((_, i) => i !== index);
  }

  create(): void {
    if (!this.model.name.trim()) {
      this.actionError.set(this.t('growth.journey.brandVoice.errors.nameRequired'));
      return;
    }

    this.saving.set(true);
    this.actionError.set(null);
    this.journey.createBrandVoiceProfile(this.buildRequest()).subscribe({
      next: created => {
        this.saving.set(false);
        this.router.navigate(['/growth/journey/brand-voice-profiles', created.id]);
      },
      error: err => {
        this.saving.set(false);
        this.actionError.set(this.resolveActionError(err));
      }
    });
  }

  save(): void {
    const current = this.profile();
    if (!current || !this.model.name.trim()) {
      return;
    }

    this.saving.set(true);
    this.actionError.set(null);
    this.journey.updateBrandVoiceProfile(current.id, this.buildRequest()).subscribe({
      next: updated => {
        this.profile.set(updated);
        this.hydrateModel(updated);
        this.saving.set(false);
        this.messages.add({ severity: 'success', summary: this.t('common.saved') });
      },
      error: err => {
        this.saving.set(false);
        this.actionError.set(this.resolveActionError(err));
      }
    });
  }

  activate(): void {
    const current = this.profile();
    if (!current) {
      return;
    }

    this.activating.set(true);
    this.actionError.set(null);
    this.journey.activateBrandVoiceProfile(current.id).subscribe({
      next: updated => {
        this.profile.set(updated);
        this.hydrateModel(updated);
        this.activating.set(false);
        this.messages.add({ severity: 'success', summary: this.t('growth.journey.brandVoice.actions.activated') });
      },
      error: err => {
        this.activating.set(false);
        this.actionError.set(this.resolveActionError(err));
      }
    });
  }

  setDefault(): void {
    const current = this.profile();
    if (!current) {
      return;
    }

    this.settingDefault.set(true);
    this.actionError.set(null);
    this.journey.setDefaultBrandVoiceProfile(current.id).subscribe({
      next: updated => {
        this.profile.set(updated);
        this.hydrateModel(updated);
        this.settingDefault.set(false);
        this.messages.add({ severity: 'success', summary: this.t('growth.journey.brandVoice.actions.defaultSet') });
      },
      error: err => {
        this.settingDefault.set(false);
        this.actionError.set(this.resolveActionError(err));
      }
    });
  }

  archive(): void {
    const current = this.profile();
    if (!current) {
      return;
    }

    this.archiving.set(true);
    this.actionError.set(null);
    this.journey.archiveBrandVoiceProfile(current.id).subscribe({
      next: updated => {
        this.profile.set(updated);
        this.hydrateModel(updated);
        this.archiving.set(false);
        this.messages.add({ severity: 'success', summary: this.t('growth.journey.brandVoice.actions.archived') });
      },
      error: err => {
        this.archiving.set(false);
        this.actionError.set(this.resolveActionError(err));
      }
    });
  }

  t(key: string): string {
    return this.i18n.translate(key);
  }

  private loadProfile(id: string): void {
    this.loading.set(true);
    this.error.set(null);
    this.journey.getBrandVoiceProfile(id).subscribe({
      next: item => {
        this.profile.set(item);
        this.hydrateModel(item);
        this.loading.set(false);
      },
      error: () => {
        this.error.set(this.t('common.notFound'));
        this.loading.set(false);
      }
    });
  }

  private hydrateModel(item: GrowthBrandVoiceProfileDetail): void {
    this.model = {
      name: item.name,
      tonePresetNumeric: brandVoiceTonePresetNumericFromName(item.tonePreset),
      locale: item.locale,
      sampleCopy: item.sampleCopy ?? ''
    };
    this.toneAttributes = this.parseToneAttributes(item.toneAttributesJson);
    this.doList = this.parseStringList(item.doListJson);
    this.dontList = this.parseStringList(item.dontListJson);
  }

  private buildRequest() {
    return {
      name: this.model.name.trim(),
      tonePreset: brandVoiceTonePresetNameFromNumeric(this.model.tonePresetNumeric),
      toneAttributesJson: JSON.stringify(this.toneAttributes),
      doListJson: JSON.stringify(this.doList),
      dontListJson: JSON.stringify(this.dontList),
      sampleCopy: this.model.sampleCopy.trim() || null,
      locale: this.model.locale
    };
  }

  private parseToneAttributes(json: string): ToneAttributes {
    try {
      const parsed = JSON.parse(json) as Partial<ToneAttributes>;
      return {
        formality: parsed.formality ?? 5,
        humor: parsed.humor ?? 3,
        empathy: parsed.empathy ?? 5,
        authority: parsed.authority ?? 5
      };
    } catch {
      return { formality: 5, humor: 3, empathy: 5, authority: 5 };
    }
  }

  private parseStringList(json: string): string[] {
    try {
      const parsed = JSON.parse(json);
      return Array.isArray(parsed) ? parsed.filter(item => typeof item === 'string') : [];
    } catch {
      return [];
    }
  }

  private resolveActionError(error: unknown): string {
    const code = this.extractErrorCode(error);
    if (code === 'growth_brand_voice_name_required') {
      return this.t('growth.journey.brandVoice.errors.nameRequired');
    }

    if (code === 'growth_brand_voice_archived_immutable') {
      return this.t('growth.journey.brandVoice.errors.archivedImmutable');
    }

    return code ?? this.t('common.error');
  }

  private extractErrorCode(error: unknown): string | null {
    if (!(error instanceof HttpErrorResponse)) {
      return null;
    }

    const body = error.error as Record<string, unknown> | null;
    if (!body) {
      return null;
    }

    if (typeof body['error'] === 'string') {
      return body['error'];
    }

    if (typeof body['Error'] === 'string') {
      return body['Error'];
    }

    return null;
  }
}
